import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useOtherCarAssignmentApi } from "../../http/otherCarAssignment/api";
import { OtherCarAssignment } from "../../http/otherCarAssignment/types";
import { PaginationQuery } from "../../types";
import { formatDateNumeric, isWithinLast48h } from "../../types/utils";
import CustomLink from "../ui/CustomLink";
import Header from "../ui/Header";
import { getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import Table from "../ui/Table";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CreateOtherCarAssignmentButton from "./CreateOtherCarAssignment";
import DeleteOtherCarAssignmentDialog from "./DeleteOtherCarAssignmentDialog";
import EditOtherCarAssignmentDialog from "./EditOtherCarAssignmentDialog";
import OtherCarAssignmentHistoryDialog from "./OtherCarAssignmentHistoryDialog";

const OtherCarAssignments: FC = () => {
  const i18n = useI18n();
  const otherCarAssignmentApi = useOtherCarAssignmentApi();
  const userSettings = useUserSettings();
  const filters = useListFilters({ isSearchable: true });
  const dispatch = useContext(NotificationDispatch);
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [itemToDelete, setItemToDelete] = useState<OtherCarAssignment>();
  const [itemToEdit, setItemToEdit] = useState<OtherCarAssignment>();
  const [itemToShowVersions, setItemToShowVersions] = useState<OtherCarAssignment>();

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const result = otherCarAssignmentApi.useAdminOtherCarAssignment(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  useEffect(() => {
    result.error && dispatch(showErrorNotification(i18n, result.error));
  }, [result.error]);

  const filterOptions = {
    get: otherCarAssignmentApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const content = (
    <>
      <Table<OtherCarAssignment>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: userSettings.permissions.otherCarAssignment.insert && <CreateOtherCarAssignmentButton />,
        }}
        head={
          <>
            <Table.Th
              title={i18n.t.common.car}
              fieldName="carPool.car"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            <Table.Th
              title={i18n.t.subsidiaries.singular}
              fieldName="carPool.car.subsidiary.name"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            <Table.Th title={i18n.t.common.startDate} fieldName="carPool.startDate" order={orderOptions} />
            <Table.Th title={i18n.t.common.endDate} fieldName="carPool.endDate" order={orderOptions} />
            <th></th>
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          setItemToEdit(item);
        }}
        renderRow={(item) => (
          <>
            <td>
              {isWithinLast48h(item.createdAt) && <NewItemIcon />}
              {userSettings.permissions.cars.read ? (
                <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.currentPlateNumber}</CustomLink>
              ) : (
                item.car.currentPlateNumber
              )}
            </td>
            <td>
              {item.car.carSubsidiary && (
                <CustomLink to={`/admin/subsidiaries/${item.car.carSubsidiary.subsidiary.id}`}>
                  {item.car.carSubsidiary.subsidiary.name}
                </CustomLink>
              )}
            </td>
            <td>{!!item.startDate && formatDateNumeric(item.startDate, i18n.language)}</td>
            <td>{!!item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
          </>
        )}
        rowActions={(item) => [
          ...(userSettings.permissions.otherCarAssignment.update
            ? [getEditAction(i18n, () => setItemToEdit(item))]
            : []),
          getHistoryAction(i18n, () => setItemToShowVersions(item)),
          ...(userSettings.permissions.otherCarAssignment.delete
            ? [getDeleteAction(i18n, () => setItemToDelete(item), true)]
            : []),
        ]}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!itemToDelete && (
        <DeleteOtherCarAssignmentDialog otherCarAssigment={itemToDelete} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToEdit && (
        <EditOtherCarAssignmentDialog otherCarAssignmentId={itemToEdit.id} onClose={() => setItemToEdit(undefined)} />
      )}

      {!!itemToShowVersions && (
        <OtherCarAssignmentHistoryDialog
          itemToShowVersions={itemToShowVersions}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );

  return (
    <>
      <Header title={i18n.t.otherCarAssignments.plural} />
      <Panel>{content}</Panel>
    </>
  );
};

export default OtherCarAssignments;
